// Initialize ErrorReporter ASAP, before other imports
import { ErrorReporter } from './infrastructure/adapter/ErrorReporter';
import config from './config';
const errorReporter = new ErrorReporter({
  apiKey: config.bugsnagApiKey,
  appVersion: config.appVersion,
  releaseStage: config.bugsnagReleaseStage || 'development',
  ...(!config.bugsnag ? { beforeSend: () => false } : {}),
});

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { useStrict } from 'mobx';
import { Provider } from 'mobx-react';
import { MuiThemeProvider } from 'material-ui/styles';

import { HttpClient } from './infrastructure/adapter/HttpClient';
import { BrowserHistoryHandler } from './infrastructure/adapter/BrowserHistoryHandler';
import { ZopimHandler } from './infrastructure/adapter/ZopimHandler';
import {
  CurrentUserRepository,
  GoalRepository,
  KaizenLabServiceRepository,
  OrganizationRepository,
  PortfolioReportRepository,
} from './domain/repository';
import {
  CurrentUserStore,
  GoalStore,
  IStore,
  KaizenLabServiceStore,
  OrganizationStore,
  PortfolioReportStore,
  UiState,
} from './domain/store';
import { AppContainer } from './presentation/component/AppContainer';
import {
  AuthenticationUsecase,
  GoalUsecase,
  HistoryHandlerUsecase,
  IUsecase,
  KaizenLabServiceUsecase,
  OrganizationUsecase,
  PortfolioReportUsecase,
} from './application/usecase';

import { Application } from './Application';
import { muiTheme } from './muiTheme';
import registerServiceWorker from './registerServiceWorker';

document.addEventListener('DOMContentLoaded', async () => {
  const httpClient = new HttpClient({ baseUrl: config.api_base });
  const browserHistoryHandler = new BrowserHistoryHandler();
  const zopimHandler = new ZopimHandler();
  const currentUserRepository = new CurrentUserRepository({ httpClient });
  const goalRepository = new GoalRepository({ httpClient });
  const kaizenLabServiceRepository = new KaizenLabServiceRepository({ httpClient });
  const organizationRepository = new OrganizationRepository({ httpClient });
  const portfolioReportRepository = new PortfolioReportRepository({ httpClient });
  const currentUserStore = new CurrentUserStore();
  const goalStore = new GoalStore();
  const kaizenLabServiceStore = new KaizenLabServiceStore();
  const organizationStore = new OrganizationStore();
  const portfolioReportStore = new PortfolioReportStore();
  const uiState = new UiState();
  const authenticationUsecase = new AuthenticationUsecase({
    currentUserRepository,
    currentUserStore,
    zopimHandler,
    errorReporter,
    uiState,
  });
  const goalUsecase = new GoalUsecase({
    goalRepository,
    goalStore,
    organizationStore,
    uiState,
  });
  const historyHandlerUsecase = new HistoryHandlerUsecase({ browserHistoryHandler });
  const kaizenLabServiceUsecase = new KaizenLabServiceUsecase({
    kaizenLabServiceRepository,
    kaizenLabServiceStore,
    organizationStore,
    uiState,
  });
  const organizationUsecase = new OrganizationUsecase({
    organizationRepository,
    organizationStore,
    uiState,
  });
  const portfolioReportUsecase = new PortfolioReportUsecase({
    portfolioReportRepository,
    portfolioReportStore,
    uiState,
  });

  const application = new Application({
    errorReporter,
    authenticationUsecase,
    goalUsecase,
    historyHandlerUsecase,
    kaizenLabServiceUsecase,
    organizationUsecase,
    portfolioReportUsecase,
    goalStore,
    organizationStore,
    portfolioReportStore,
    uiState,
  });

  const store: IStore = {
    currentUserStore,
    goalStore,
    kaizenLabServiceStore,
    organizationStore,
    portfolioReportStore,
    uiState,
  };

  const usecase: IUsecase = {
    portfolioReportUsecase,
  };

  // Use MobX in strict mode
  useStrict(true);

  // boot up 👢
  application.boot();

  // Render root component
  ReactDOM.render(
    <Provider store={store} usecase={usecase}>
      <MuiThemeProvider theme={muiTheme}>
        <AppContainer />
      </MuiThemeProvider>
    </Provider>,
    document.getElementById('root') as HTMLElement,
  );

  // Setup zopim
  if (config.zopim) {
    zopimHandler.addTags('experience-portfolio');
    zopimHandler.minimize();
  } else {
    zopimHandler.hideAll();
  }

  // Register service worker
  registerServiceWorker();
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/index.tsx